import { ga } from '@helpers/dataLayer';
import IconArrowHead from '@img/icons/icon-arrow-head.svg';
import FacebookIcon from '@img/icons/social/facebook.svg';
import InstagramIcon from '@img/icons/social/instagram.svg';
import LinkedinIcon from '@img/icons/social/linkedin.svg';
import TikTokIcon from '@img/icons/social/tiktok.svg';
import YoutubeIcon from '@img/icons/social/youtube.svg';
import CultureFlag from '@stories/Elements/Misc/CultureFlag/CultureFlag';
import { Column, Grid } from '@stories/Settings/Grids/Grid.styles';
import { PageLanguageItem } from '@stories/Widgets/Footer/Footer';
import React, { useState } from 'react';
import s from './FooterBottom.styles';

export interface FooterBottomProps {
  items: CTAModel[];
  socialItems: CTAModel[];
  translations: Record<string, string | undefined>;
  documentCulture: string;
  cultures: PageLanguageItem[];
}

type SocialMediaT = 'Instagram' | 'YouTube' | 'Facebook' | 'LinkedIn' | 'TikTok' | '';

const FooterBottom: React.FC<FooterBottomProps> = ({ items, socialItems, translations, documentCulture, cultures }) => {
  const [visibleCultures, setVisibleCultures] = useState<boolean>(false);

  const renderSocialIcon = (name: string): React.ReactNode => {
    switch (name.toLowerCase()) {
      case 'instagram': {
        return <InstagramIcon />;
      }

      case 'youtube': {
        return <YoutubeIcon />;
      }

      case 'facebook': {
        return <FacebookIcon />;
      }

      case 'linkedin': {
        return <LinkedinIcon />;
      }

      case 'tiktok': {
        return <TikTokIcon />;
      }

      default: {
        return null;
      }
    }
  };

  const getGTMEvent = (name: string): SocialMediaT => {
    switch (name.toLowerCase()) {
      case 'instagram':
        return 'Instagram';
      case 'youtube':
        return 'YouTube';
      case 'facebook':
        return 'Facebook';
      case 'linkedin':
        return 'LinkedIn';
      case 'tiktok':
        return 'TikTok';
      default:
        return '';
    }
  };

  return (
    <s.Wrapper>
      <Grid.Default>
        <Column.Full>
          <s.Row>
            <small dangerouslySetInnerHTML={{ __html: translations['copyright'] || '' }}></small>
            <s.SocialLinks>
              {socialItems.map((item: CTAModel) => (
                <s.SocialIcon
                  onClick={(e) => {
                    e.preventDefault();
                    ga({
                      event: 'click_social',
                      cta_name: getGTMEvent(item.text),
                    });
                    window.open(item.url, item.target ?? '_self');
                  }}
                  key={item.id}
                  href={item.url}
                  target={item.target ?? '_self'}
                  aria-label={item.text}
                >
                  <span>{item.text}</span> {renderSocialIcon(item.text)}
                </s.SocialIcon>
              ))}
            </s.SocialLinks>
            <s.CultureWrapper>
              <s.CultureSelect active={visibleCultures} onClick={() => setVisibleCultures(!visibleCultures)}>
                <CultureFlag cultureCode={documentCulture} />
                <s.Icon
                  initial={{ rotate: 0 }}
                  animate={visibleCultures ? { rotate: 180 } : { rotate: 0 }}
                  transition={{
                    duration: 0.28,
                    ease: 'easeInOut',
                  }}
                >
                  <IconArrowHead />
                </s.Icon>
              </s.CultureSelect>
              <s.CultureList visible={visibleCultures}>
                <s.CultureListInner>
                  {cultures.map((culture) => (
                    <s.CultureItem key={culture.id}>
                      <s.CultureLink
                        onClick={(e) => {
                          e.preventDefault();
                          ga({
                            event: 'click_language_select',
                            country_language: culture.cultureCode,
                            langauge_selector_type: 'footer',
                          });
                          window.location.href = culture.url;
                        }}
                        href={culture.url}
                      >
                        <CultureFlag cultureCode={culture.cultureCode} />
                        {culture.cultureName}
                      </s.CultureLink>
                    </s.CultureItem>
                  ))}
                </s.CultureListInner>
              </s.CultureList>
            </s.CultureWrapper>
          </s.Row>
          <s.List>
            {items.map((item) => (
              <s.Item key={item.id}>
                <s.Link href={item.url}>{item.text}</s.Link>
              </s.Item>
            ))}
            <s.Item>
              <s.Link id={'ot-sdk-btn'} className={'ot-sdk-show-settings overwrite'}>
                {translations['footer.cookie-settings']}
              </s.Link>
            </s.Item>
          </s.List>
        </Column.Full>
      </Grid.Default>
    </s.Wrapper>
  );
};

export default FooterBottom;
